import { $customerAuth, decodeToken } from '@/lib/auth';
import {
  $activeChatId,
  $isLoading,
  append,
  setActiveChat,
  setChatHistory,
  setInput,
  setLoadingChat,
  setMessages,
  setOnboardingSeen,
  updateLastMessage,
  upsertChat,
} from '@/lib/chat';
import { getSettings } from '@/lib/hec';
import { getShortID } from '@/router';
import { atom } from 'nanostores';
import { toast } from 'sonner';
import { getSeoTitleForSubdomain } from './_helper';
import type { ChatEngineOptions, ChatErrorType, ClientMessage, ServerMessage } from './chat-protocol';

const wsApiURL = import.meta.env.VITE_WS_API_URL;

export class ChatEngine {
  ws?: WebSocket;
  response = '';

  private onTextChunk?: ChatEngineOptions['onTextChunk'];
  private onTextDone?: ChatEngineOptions['onTextDone'];
  private onError?: ChatEngineOptions['onError'];
  private onSendMessage?: ChatEngineOptions['onSendMessage'];
  private onDisconnect: NonNullable<ChatEngineOptions['onDisconnect']>;

  constructor({
    onTextChunk,
    onTextDone,
    onSendMessage,
    onError,
    onDisconnect = () => console.warn('no disconnect handler set'),
  }: ChatEngineOptions) {
    this.onTextChunk = onTextChunk;
    this.onTextDone = onTextDone;
    this.onError = onError;
    this.onSendMessage = onSendMessage;
    this.onDisconnect = onDisconnect;
    $customerAuth.subscribe((e) => {
      const shortID = getShortID();
      const auth = shortID ? e?.[shortID] : undefined;
      if (!auth && this.ws) {
        this.ws.onclose = null;
        this.ws.close();
        this.ws = undefined;
      }
    });
  }

  private send(msg: ClientMessage) {
    this.ws?.send(JSON.stringify(msg));
  }

  async init(): Promise<void> {
    if (this.ws) {
      return;
    }
    const shortID = getShortID();
    const auth = shortID ? $customerAuth.get()[shortID] : undefined;
    if (!shortID || !auth?.accessToken) {
      return Promise.reject('no_auth');
    }
    const { accessToken } = auth;
    const settings = await getSettings(shortID);
    const { appContent, frontendUrl } = settings;
    let { subdomain: seoTitle } = settings;
    seoTitle = getSeoTitleForSubdomain(seoTitle);

    // dafür kommen wir in die hölle
    const requests = appContent.map((content: { type: string; shortID: string }) =>
      getSettings(content.shortID)
        .then((s: { subdomain: string }) => s.subdomain)
        .catch(() => 'eafg.big-why'),
    );
    const additionalSources: string[] = await Promise.all(requests);

    const decoded = decodeToken(shortID);
    if (!decoded) {
      return Promise.reject('no_auth');
    }
    const { data, sub: accountID } = decoded;

    const ws = new WebSocket(`${wsApiURL}/chat/${data.backendID}$${data.customerID}`);
    this.ws = ws;

    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        ws.close();
        this.onError?.('socket_timeout');
        reject(new Error('WebSocket connection timeout'));
      }, 10 * 1000);
      ws.onerror = (err) => {
        console.log('socket err', err);
        this.onError?.('socket_cannot_connect');
        reject(err);
      };
      ws.onopen = () =>
        this.send({
          type: 'properties',
          properties: {
            seoTitle,
            allowedWebsites: [frontendUrl.replace('https://', '')],
            availableServers: ['dsb_api_server', 'retrieval_server'],
            accountAgeDays: 0,
            additionalSources,
            accessToken,
            accountID,
            supportsMultiChat: true,
            ...data,
          },
        });
      ws.onclose = () => this.onDisconnect();

      const readyListener = (event: MessageEvent) => {
        const m = JSON.parse(event.data) as ServerMessage;
        if (m.type === 'log' && m.value === 'chatagent_ready') {
          clearTimeout(timeout);
          ws.removeEventListener('message', readyListener);
          resolve();
        }
      };
      ws.addEventListener('message', readyListener);
    });

    ws.onmessage = (event) => this.handleMessage(JSON.parse(event.data) as ServerMessage);

    // On (re)connect, restore the active chat — the backend resets its
    // active_chat_id per connection, so without this the next message would
    // silently start a new chat.
    const activeChatId = $activeChatId.get();
    if (activeChatId) {
      this.send({ type: 'switch_chat', chat_id: activeChatId });
    }
  }

  private handleMessage(msg: ServerMessage) {
    switch (msg.type) {
      case 'chatbot':
        this.response += msg.text;
        this.onTextChunk?.(this.response, msg.run_id, null);
        break;
      case 'deeplinks':
      case 'documents':
        this.onTextChunk?.(this.response, msg.run_id, msg.documents, msg.deeplinks);
        break;
      case 'chatbot_done':
        $isLoading.set(false);
        this.onTextDone?.(msg);
        break;
      case 'error':
        console.log('error', msg);
        $isLoading.set(false);
        setLoadingChat(false);
        break;
      case 'log':
        break;
      case 'websocket_disconnect':
        // do not call onDisconnect multiple times
        break;
      case 'action':
        append({
          id: Date.now() + '',
          run_id: msg.run_id || '?',
          role: 'assistant',
          content: msg.action.content,
          createdAt: new Date(),
          action: msg.action,
        });
        break;
      // --- multi-chat ---
      case 'chat_created':
        upsertChat({ chat_id: msg.chat_id, chat_title: msg.chat_title });
        setActiveChat(msg.chat_id);
        break;
      case 'current_chat':
        upsertChat(msg.chat);
        setActiveChat(msg.chat.chat_id);
        setChatHistory(msg.history);
        setLoadingChat(false);
        break;
      case 'session':
        // anon-only session echo; nothing to do for the member flow
        break;
      default:
        console.log('unexpected msg type', (msg as { type?: string }).type);
    }
  }

  async sendMessage(msg: string): Promise<void> {
    if (!msg) {
      console.warn('tried to send empty message. ignoring');
      return;
    }
    this.response = '';
    if ($isLoading.get()) {
      return;
    }
    $isLoading.set(true);

    setOnboardingSeen();

    this.onSendMessage?.(msg);
    await this.init();
    this.send({ type: 'input_text', text: msg });
  }

  async chooseActionOption(user_decision: number): Promise<void> {
    // update the action message with user_decision (to light up the chosen button)
    setMessages((messages) => {
      const last = messages.at(-1)!;
      const lastMessage = { ...last, action: { ...last.action!, user_decision } };
      return [...messages.slice(0, -1), lastMessage];
    });
    // after selecting an action, we need another hidden message for the typing indicator
    append({
      id: Date.now() + '',
      role: 'hidden',
      content: '',
      createdAt: new Date(),
    });
    this.send({ type: 'action_response', content: { user_decision } });
  }

  /** Open an existing chat. The backend replies `current_chat` with its history. */
  async switchChat(chatId: string): Promise<void> {
    if ($activeChatId.get() === chatId) {
      return;
    }
    setOnboardingSeen(); // picking an existing chat means we're past onboarding
    setLoadingChat(true);
    try {
      await this.init();
      this.send({ type: 'switch_chat', chat_id: chatId });
    } catch {
      setLoadingChat(false);
    }
  }

  /** Start a fresh chat. The chat is created on the backend once the first
   * message is sent (then it answers with `chat_created`). */
  async newChat(): Promise<void> {
    setOnboardingSeen();
    setActiveChat(null);
    setChatHistory([]); // show the greeting like a fresh chat
    await this.init();
    this.send({ type: 'new_chat' });
  }
}

/** true once the WebSocket drops — drives the connection-lost overlay. */
export const $disconnected = atom<boolean>(false);

const errors: Record<ChatErrorType, string> = {
  socket_cannot_connect: 'Verbindungsaufbau fehlgeschlagen',
  socket_timeout: 'Verbindungsaufbau fehlgeschlagen',
  no_auth: 'Bitte erneut einloggen',
};

export const chatEngine = new ChatEngine({
  onError(type) {
    const msg = errors[type] || 'Unbekannter Fehler';
    console.warn(`ChatEngine error: ${msg}`);
    toast.error(msg);
  },
  onSendMessage(msg) {
    append({
      id: Date.now() + '',
      role: 'user',
      content: msg,
      createdAt: new Date(),
    });
    setInput('');
    append({
      id: Date.now() + '',
      role: 'hidden',
      content: '',
      createdAt: new Date(),
    });
  },
  onTextChunk(response, run_id, documents = null, deeplinks = null) {
    updateLastMessage('assistant', response, run_id, documents ?? undefined, deeplinks ?? undefined);
  },
  onDisconnect() {
    console.warn('disconnected');
    $isLoading.set(false);
    $disconnected.set(true);
    toast.error('Du wurdest vom MY COACH getrennt.', {
      duration: Infinity,
      description: 'Die Verbindung zum MY COACH wurde unterbrochen. Bitte lade die Seite neu, um fortzufahren.',
      action: {
        label: 'Neu laden',
        onClick: () => location.reload(),
      },
    });
  },
});
